import { FormArray, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import _ from 'lodash';
import { DeepPartial } from '../../../../ts-extensions';
import { IncidentFormControls, IncidentFormError, IncidentFormGroup, IncidentFormValues, TheftTypeFormGroup } from '../../../../types';

/**
 * Validate a link input in the form.
 *
 * Check if the provided value is a valid URL using a regular expression.
 * The URL must:
 * - Start with http:// or https://
 * - Contain a valid domain name
 * - Optionally include a port number (maybe not?)
 * - Optionally include a path
 *
 * @param control The FormControl to validate
 * @returns An object with an 'invalidLink' property if the link is invalid, null otherwise
 */
export const linkValidator = (control: FormControl) => {
  const value = control.value;
  const linkPattern = /^(http|https):\/\/[a-zA-Z0-9]+([\\-\\.]{1}[a-zA-Z0-9]+)*\.[a-zA-Z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;
  if (value && !linkPattern.test(value)) {
    return { invalidLink: true };
  }
  return null;
};

export function atLeastOneTheftTypeValidator(theftType: IncidentFormControls['theftType']): ValidationErrors | null {
  const group = theftType as TheftTypeFormGroup;
  const isCancellation = group.controls.is_cancellation.value;
  const isUnderringing = group.controls.is_underringing.value;
  const isWalkout = group.controls.is_walkout.value;
  const isCashier = group.controls.is_cashier.value;
  const atLeastOne = [isCancellation || isUnderringing || isWalkout || isCashier].some(Boolean);
  return atLeastOne ? null : { [IncidentFormError.THEFT_TYPE_REQUIRED]: true };
}

export function atLeastOneExtraLinkValidator(extraLinks: IncidentFormControls['extraLinks']): ValidationErrors | null {
  const formArray = extraLinks as FormArray;
  const nonEmptyLinks = formArray.controls.map((c) => c.value?.trim()).filter(Boolean).length;
  return nonEmptyLinks > 0 ? null : { [IncidentFormError.EXTRA_LINKS_REQUIRED]: true };
}

export function duplicateExtraLinksValidator(extraLinks: IncidentFormControls['extraLinks']): ValidationErrors | null {
  const values = _.chain(extraLinks.controls)
    .map((c) => c.value?.trim())
    .filter(Boolean)
    .value();
  const hasDuplicate = values.length !== _.uniq(values).length;
  return hasDuplicate ? { [IncidentFormError.EXTRA_LINKS_DUPLICATE]: true } : null;
}

export function validateComments(comments: FormControl): ValidationErrors | null {
  if (!comments.value?.trim()) {
    return { [IncidentFormError.COMMENTS_REQUIRED]: true };
  }
  if (comments.value.length < 10) {
    return { [IncidentFormError.COMMENTS_MIN_LENGTH]: true };
  }
  if (comments.value.length > 2000) {
    return { [IncidentFormError.COMMENTS_MAX_LENGTH]: true };
  }
  return null;
}

export const incidentFormValidator: ValidatorFn = (form: IncidentFormGroup): ValidationErrors | null => {
  const { isTheft, instance_at, theftType, uniqueInstancePhoto, existingUniqueInstancePhoto, useKioskPhoto } = form.controls;

  const errors: ValidationErrors = {};

  if (!isTheft.value) {
    return null;
  }

  if (!instance_at.value) {
    errors[IncidentFormError.INSTANCE_AT_REQUIRED] = true;
  }

  const theftTypeErrors = theftType ? atLeastOneTheftTypeValidator(theftType) : null;
  if (theftTypeErrors) Object.assign(errors, theftTypeErrors);

  const hasNewPhoto = uniqueInstancePhoto.value.length > 0;
  const hasExistingPhoto = existingUniqueInstancePhoto.value.length > 0;
  const usingKioskPhoto = useKioskPhoto.value;
  if (!hasNewPhoto && !hasExistingPhoto && !usingKioskPhoto) {
    errors[IncidentFormError.INSTANCE_PHOTO_REQUIRED] = true;
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

export const defaultIncidentFormValues: IncidentFormValues = {
  isTheft: false,
  reviewOutcomeId: null,
  comments: '',
  commentsRating: 0,
  suspectedItems: [],
  matchingCase: 'new_case',
  additionalStolenValue: 0,
  instance_at: null,
  theftType: {
    is_cancellation: false,
    is_underringing: false,
    is_walkout: false,
    is_cashier: false,
  },
  uniqueInstancePhoto: null,
  existingUniqueInstancePhoto: [],
  reports: null,
  existingReports: [],
  extraLinks: [''],
  didNotReturn: false,
  selectedImages: [],
  useKioskPhoto: false,
};

export function normalizeIncidentFormValues(values: DeepPartial<IncidentFormValues>): IncidentFormValues {
  return {
    isTheft: values.isTheft ?? false,
    reviewOutcomeId: values.reviewOutcomeId ?? null,
    comments: values.comments ?? '',
    commentsRating: 0,
    suspectedItems:
      values.suspectedItems?.map((i) => ({
        id: i.id,
        description: i.description ?? '',
        price: i.price ?? 0,
        total: i.total ?? 0,
      })) ?? [],
    matchingCase: values.matchingCase ?? '',
    additionalStolenValue: values.additionalStolenValue ?? 0,
    instance_at: values.instance_at instanceof Date ? values.instance_at : null,
    theftType: {
      is_cancellation: values.theftType?.is_cancellation ?? false,
      is_underringing: values.theftType?.is_underringing ?? false,
      is_walkout: values.theftType?.is_walkout ?? false,
      is_cashier: values.theftType?.is_cashier ?? false,
    },
    uniqueInstancePhoto: (values.uniqueInstancePhoto as File[] | null) ?? null,
    existingUniqueInstancePhoto: values.existingUniqueInstancePhoto ?? [],
    reports: (values.reports as File[] | null) ?? null,
    existingReports: values.existingReports ?? [],
    extraLinks: values.extraLinks ?? [],
    didNotReturn: values.didNotReturn ?? false,
    selectedImages: (values.selectedImages as string[] | undefined) ?? [],
    useKioskPhoto: values.useKioskPhoto ?? false,
  };
}
